import Slider from "react-slick";
import { VerticalVideoItem } from "./designs/verticalVideoItem/VerticalVideoItem";
import { SiteItemData } from "../../interfaces";
import { useRef } from "react";
import { useVerticalItemSizing } from "./hooks/useVerticalItemSizing";

interface VerticalVideoGalleryProps {
    isLTR: boolean;
    domain: string;
    isMobile: boolean;
    videoItems: SiteItemData[];
    languageCode: string;
    showPrerollAds: boolean;
    isTitleVisible: boolean;
    isSubTitleVisible: boolean;
    selectedVideoIndex: number;
    currentPlayingVideo: number;
    onVideoOverlayClose: () => void;
    setCurrentPlayingVideo: (index: number) => void;
    pushGA4Event: (action: string, isFeedDisplay: boolean) => void;
    headerClassName?: string;
    ItemComponent?: (props: any) => any;
}

export const VerticalVideoGallery = (props: VerticalVideoGalleryProps) => {

    const { domain, videoItems, languageCode, showPrerollAds, isTitleVisible, currentPlayingVideo, isSubTitleVisible, onVideoOverlayClose, isLTR, pushGA4Event, selectedVideoIndex, setCurrentPlayingVideo, isMobile, headerClassName, ItemComponent } = props;
    const Item = ItemComponent || VerticalVideoItem;
    const sliderRef: any = useRef<any>(null);

    // Calculate sizing once for all items
    const { containerStyle, dynamicHeight, dynamicWidth } = useVerticalItemSizing({ isMobile, headerClassName });

    const onNextSlide = () => {
        if (isMobile) {
            onChangeVideoHandler();
            sliderRef.current.slickNext();
            pushGA4Event("swipe_up", true);
        } else {
            if (isLTR) {
                sliderRef.current.slickNext();
            } else {
                sliderRef.current.slickPrev();
            }
        }
    }

    const onFeedArrowClick = () => {
        const activeVideoElement = document.querySelector(".vertical-video-overlay-player .slick-active .vertical-video-item #player .fp-engine") as HTMLVideoElement;
        if (activeVideoElement) activeVideoElement.pause();
    }

    const onStopPlayingVideoHandler = () => {
        const playingVideoElement = document.querySelector(".vertical-video-overlay-player .slick-slide:not(.slick-active) .vertical-video-item #player.is-playing .fp-engine") as HTMLVideoElement;
        if (playingVideoElement) {
            playingVideoElement.currentTime = 0;
            playingVideoElement.pause();
        };
    }

    const onChangeVideoHandler = () => {
        setTimeout(() => {
            const videoElement = document.querySelector(".vertical-video-overlay-player .slick-active .vertical-video-item #player.is-paused .fp-engine") as HTMLVideoElement;
            if (videoElement) {
                videoElement.currentTime = 0;
                videoElement.click();
            };
        }, 600)
    }
    const beforeSliderChangeHandler = (current, next) => {
        if (isMobile) {
            const userSwipeDirection = next > current ? "up" : "down";
            pushGA4Event(`swipe_${userSwipeDirection}`, true);
        } else {
            const nextSlide = isLTR ? next : Math.abs(next - videoItems.length + 1);
            setCurrentPlayingVideo(nextSlide);
            onFeedArrowClick();
        }
    }

    const afterSliderChangeHandler = (current) => {
        if (isMobile) {
            setCurrentPlayingVideo(current);
            onStopPlayingVideoHandler();
        }
    }

    const NextVideoText = () => {
        const { languageCode } = props
        if (languageCode.includes("en")) return "next video"
        else if (languageCode.includes("ru")) return `Следующее видео`
        else return `לסרטון הבא`
    }

    // In RTL mode, react-slick reverses internal indices. Convert initialSlide
    // so the correct video is shown on first render (matches beforeChange mapping).
    const isRtlSlider = !isMobile && !isLTR;
    const initialSlide = isRtlSlider
        ? videoItems.length - 1 - selectedVideoIndex
        : selectedVideoIndex;

    const feedDisplaySettings = {
        speed: 500,
        infinite: false,
        slidesToShow: 1,
        draggable: false,
        slidesToScroll: 1,
        vertical: isMobile,
        verticalSwiping: isMobile,
        onSwipe: onChangeVideoHandler,
        rtl: isMobile ? false : !isLTR,
        initialSlide,
        afterChange: (current) => afterSliderChangeHandler(current),
        beforeChange: (current, next) => beforeSliderChangeHandler(current, next),
        nextArrow: isMobile ? null : <SampleNextArrow currentPlayingVideo={currentPlayingVideo} listLength={videoItems.length} pushGA4Event={pushGA4Event} isLTR={isLTR} onClick={onFeedArrowClick} />,
        prevArrow: isMobile ? null : <SamplePrevArrow currentPlayingVideo={currentPlayingVideo} listLength={videoItems.length} pushGA4Event={pushGA4Event} isLTR={isLTR} onClick={onFeedArrowClick} />,
    };

    return (
        <Slider {...feedDisplaySettings} className="slider vertical-video-gallery" ref={sliderRef}>
            {videoItems.map((videoItem, index) =>
                typeof videoItem === 'string' ?
                    isMobile ?
                        <div className="adsWrapper">
                            <div key={index} id={videoItem} className="ads_vertical_video"></div>
                            {index < videoItems.length - 1 && <div className={`arrow arrowNext`} onClick={onNextSlide}>{NextVideoText()}</div>}
                        </div>
                        :
                        <div key={index} id={videoItem} className="ads_vertical_video"></div>
                    :
                    <Item
                        index={index}
                        domain={domain}
                        isMobile={isMobile}
                        isGalleryView={true}
                        videoItem={videoItem}
                        key={videoItem.itemId}
                        onNextSlide={onNextSlide}
                        languageCode={languageCode}
                        dynamicWidth={dynamicWidth}
                        dynamicHeight={dynamicHeight}
                        containerStyle={containerStyle}
                        showPrerollAds={showPrerollAds}
                        isTitleVisible={isTitleVisible}
                        headerClassName={headerClassName}
                        isSubTitleVisible={isSubTitleVisible}
                        currentPlayingVideo={currentPlayingVideo}
                        onVideoOverlayClose={onVideoOverlayClose}
                        shouldDisplayArrow={isMobile && index < videoItems.length - 1}
                    />
            )}
        </Slider>
    )
}

const SampleNextArrow = (props) => {
    const { onClick, currentPlayingVideo, pushGA4Event, isLTR, listLength } = props;
    const shouldDisplayArrow = isLTR ? (currentPlayingVideo + 1) !== listLength : currentPlayingVideo !== 0;

    const handleClick = () => {
        onClick();
        pushGA4Event("swipe_right", true);
    }

    return <button className={`slickArrow slickNext ${shouldDisplayArrow ? "" : "slickDisabled"}`} onClick={handleClick}></button>;
}

const SamplePrevArrow = (props) => {
    const { onClick, currentPlayingVideo, listLength, pushGA4Event, isLTR } = props;
    const shouldDisplayArrow = isLTR ? currentPlayingVideo !== 0 : (currentPlayingVideo + 1) !== listLength;

    const handleClick = () => {
        onClick();
        pushGA4Event("swipe_left", true);
    }

    return <button className={`slickArrow slickPrev ${shouldDisplayArrow ? "" : "slickDisabled"}`} onClick={handleClick}></button>
}